import React, { useEffect, useRef, useState } from 'react';
import { FaWhatsapp, FaEnvelope } from 'react-icons/fa';

/** Poziția (left/top, în px) la care utilizatorul a tras widgetul. Persistă între sesiuni. */
const POS_KEY = 'contactButtonsPos';
/** Marginea minimă față de marginile ferestrei la clamp. */
const MARGIN = 8;

interface Pos {
  left: number;
  top: number;
}

/** Menține widgetul în viewport (nu poate fi tras dincolo de marginile ferestrei). */
const clamp = (left: number, top: number, w: number, h: number): Pos => ({
  left: Math.min(Math.max(MARGIN, left), Math.max(MARGIN, window.innerWidth - w - MARGIN)),
  top: Math.min(Math.max(MARGIN, top), Math.max(MARGIN, window.innerHeight - h - MARGIN)),
});

const ContactButtons = () => {
  const ref = useRef<HTMLDivElement>(null);
  /** True dacă ultimul pointerdown a fost o tragere — suprimă click-ul de deschidere. */
  const draggedRef = useRef(false);

  // Poziția salvată; null = poziția implicită (stânga-jos, via clasele Tailwind).
  const [pos, setPos] = useState<Pos | null>(() => {
    try {
      const raw = localStorage.getItem(POS_KEY);
      const p = raw ? (JSON.parse(raw) as { left?: unknown; top?: unknown }) : null;
      return p && typeof p.left === 'number' && typeof p.top === 'number' ? { left: p.left, top: p.top } : null;
    } catch {
      return null;
    }
  });

  const onPointerDown = (e: React.PointerEvent) => {
    const el = ref.current;
    if (!el || e.button !== 0) return;
    const rect = el.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;
    // Offsetul cursorului în interiorul widgetului, ca să nu „sară" la tragere.
    const offX = startX - rect.left;
    const offY = startY - rect.top;
    let last: Pos | null = null;
    draggedRef.current = false;
    const onMove = (ev: PointerEvent) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      // Sub 4px e considerat click, nu tragere — altfel orice click ar muta widgetul.
      if (!draggedRef.current && Math.abs(dx) < 4 && Math.abs(dy) < 4) return;
      draggedRef.current = true;
      last = clamp(ev.clientX - offX, ev.clientY - offY, rect.width, rect.height);
      el.style.left = `${last.left}px`;
      el.style.top = `${last.top}px`;
      el.style.right = 'auto';
      el.style.bottom = 'auto';
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      if (last) {
        localStorage.setItem(POS_KEY, JSON.stringify(last));
        setPos(last);
      }
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  // Widgetul rămâne în viewport la redimensionarea ferestrei.
  useEffect(() => {
    const onResize = () => {
      setPos((p) => {
        if (!p) return p;
        const el = ref.current;
        const c = clamp(p.left, p.top, el?.offsetWidth ?? 120, el?.offsetHeight ?? 56);
        if (c.left === p.left && c.top === p.top) return p;
        localStorage.setItem(POS_KEY, JSON.stringify(c));
        return c;
      });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const openWhatsApp = () => {
    if (draggedRef.current) { draggedRef.current = false; return; }
    const message = encodeURIComponent('Buna Serban, am urmatoarea intrebare referitoare la program:');
    window.open(`https://wa.me/40770635586?text=${message}`, '_blank');
  };

  const openEmail = () => {
    if (draggedRef.current) { draggedRef.current = false; return; }
    const subject = encodeURIComponent('Intrebare program TLS');
    const body = encodeURIComponent('Buna Serban,\n\nam urmatoarea intrebare referitoare la program:\n\n');
    window.open(`mailto:office@freevox.ro?subject=${subject}&body=${body}`);
  };

  if (localStorage.getItem('ascundeButoane')) {
    return <></>;
  }

  return (
    <div
      ref={ref}
      onPointerDown={onPointerDown}
      style={pos ? { left: pos.left, top: pos.top, right: 'auto', bottom: 'auto' } : undefined}
      className={`fixed z-50 hidden touch-none select-none flex-row gap-4 md:flex ${pos ? '' : 'bottom-2 left-6'}`}
      title="Trage pentru a muta"
    >
      <button
        onClick={openWhatsApp}
        className="transform rounded-full bg-green-500 p-3 shadow-lg transition-all duration-200 hover:scale-110 hover:bg-green-600 cursor-grab active:cursor-grabbing"
        aria-label="Contact on WhatsApp"
      >
        <FaWhatsapp className="pointer-events-none text-2xl text-white" />
      </button>

      <button
        onClick={openEmail}
        className="transform rounded-full bg-blue-500 p-4 shadow-lg transition-all duration-200 hover:scale-110 hover:bg-blue-600 cursor-grab active:cursor-grabbing"
        aria-label="Send email"
      >
        <FaEnvelope className="pointer-events-none text-lg text-white" />
      </button>
    </div>
  );
};

export default ContactButtons;
